import React, { useState, useEffect } from "react";

import { useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  Spin,
  Form,
  Breadcrumb,
  Row,
  Col,
  Result,
  message,
} from "antd";
import { useLocation } from "react-router-dom";
import { BorderlessTableOutlined, PieChartOutlined } from "@ant-design/icons";
import get from "lodash/get";
import find from "lodash/find";
import assign from "lodash/assign";

import dayjs from "dayjs";
import {
  getCurrentDateRange
} from "../../utilities/report";
import HeaderSelector from "../../components/reports/breakdownAnalysis/headerSelector";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { fetchCompanyTemplates } from "../../redux/actions/templateActions";
import { fetchBreakDownGraph } from "../../redux/actions/breakdownActions";
import BreakDownGraphGrid from "../../components/graphs/breakDownGraphGrid";
import { getBreakDownQueryParams } from "../../utilities/getAndOnParams";
import requireAuth from "../../hocs/requireAuth";
import { BREAKDOWN_PERMISSION, COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <PieChartOutlined style={{ fontSize: "16px" }} />
              <span>Breakdown</span>
            </>
          ),
        },
        {
          title: "Breakdown Analysis",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
  template: undefined,
  interval: "day",
  date: getCurrentDateRange("day", dayjs()),
};

const BreakDownAnalysisScreen = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { company_uuid, template_type, template, interval, from_date, to_date } = getBreakDownQueryParams(
    location.search
  );
  const [statsConfig, setStatsConfig] = useState(undefined)
  const [config, setConfig] = useState(null);
  const [filter, setFilters] = useState(initialState);
  const [form] = Form.useForm();
  const [showEmpty, setShowEmpty] = useState(false)
  const dispatch = useDispatch();

  const getTemplates = (query) => {
    setLoading(true);
    dispatch(fetchCompanyTemplates(get(filter, "company.id"), query))
      .then((result) => {
        setConfig({
          ...config,
          templates: result,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const setinitialCompany = (company) => {
      const hasPermission = find(
        get(company, "feature_permissions", []),
        ['key', BREAKDOWN_PERMISSION]
      );
    
      if (!hasPermission) return;
      
    setFilters({
      ...filter,
      templateType: undefined,
      template: undefined,
      company,
    });

    setConfig({
      ...config,
      companies: [company],
      template_types: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template: undefined,
      Company: get(company, "id"),
      template_type: undefined,
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getBreakdownGraph = (template, query) => {
    setLoading(true);
    dispatch(fetchBreakDownGraph(template || get(filter, "template"), query)).then(result => {
      if (!result) {
        setShowEmpty(true);
        setLoading(false)
        setStatsConfig({overallStats:[{ type: "No break downs", value: 1, color: "lightgray" }],templateStats:[{ type: "No break downs", value: 1, color: "lightgray" }]})
      } else {
        if(get(result,"overallStats.length")===0){
          assign(get(result, "overallStats"),[{ type: "No break downs", value: 1, color: "lightgray" }])
        }
        if(get(result,"templateStats.length")===0){
          assign(get(result, "templateStats"),[{ type: "No break downs", value: 1, color: "lightgray" }])
        }
        setStatsConfig({
          ...result
        })
        setShowEmpty(false);
      }
      setLoading(false)
    })
      .catch(() => {
        setLoading(false)
      })
  }
  const initializeFormParams = () => {
    const dateFormat = "YYYY-MM-DD"
    const currentDateRange = interval === "day" ? [dayjs(from_date || to_date).format(dateFormat),
    dayjs(from_date || to_date).format(dateFormat)] : getCurrentDateRange(interval, from_date || to_date)
    setLoading(true);
    dispatch(fetchCompanies())
      .then((companies) => {
        const company = find(companies, ["id", company_uuid]);

        if (!company) {
          message.error("Company not found from URL param.");
          setLoading(false);
          return;
        }

        form.setFieldsValue({
          Company: company_uuid,
          templateType: parseInt(template_type),
          range: interval,
          template,
          date: currentDateRange,
        });

        dispatch(
          fetchCompanyTemplates(company_uuid, { type: parseInt(template_type) })
        )
          .then((templates) => {
            setConfig({
              ...config,
              companies,
              template_types: get(company, "template_access_list"),
              templates,
            });

            setFilters((prev) => ({
              ...prev,
              company,
              templateType: template_type,
              template,
              interval,
              date: currentDateRange,
            }));

            getBreakdownGraph(template, { from_date: get(currentDateRange, "0"), to_date: get(currentDateRange, "1") });
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
    if (company_uuid && template_type && template && interval && from_date && to_date) {
      initializeFormParams();
    }
  }, []);
  return (
    <div className={`${isMobile ? "" : "p-2"} h-full flex flex-col`}>
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Form form={form}>
          <Spin spinning={loading}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              getTemplates={getTemplates}
              getDetails={getBreakdownGraph}
              setStatsConfig={setStatsConfig}
            />
            {get(statsConfig, 'templateStats') && get(statsConfig, "overallStats") && (
              <>
                <div className="w-full px-4 md:px-8 py-6">
                  <Row gutter={16}>
                    <Col xs={24} sm={24} md={12}>
                      <BreakDownGraphGrid stats={get(statsConfig, "templateStats")} label={get(
                        find(get(config, "templates"), [
                          "id",
                          get(filter, "template"),
                        ]),
                        "name"
                      )} id="templateStats" />
                    </Col>

                    <Col xs={24} sm={24} md={12}>
                      <BreakDownGraphGrid label="Overall" stats={get(statsConfig, "overallStats")} id="overallStats" />
                    </Col>
                  </Row>

                </div>
              </>

            )}
          </Spin>
        </Form>
        {showEmpty && (
          <div className="flex flex-wrap items-center justify-center text-white h-[100%]">
            <div className="px-8 py-8">
              <Result
                title="No Breakdown graph exits for the time range"
                subTitle="Please try again with another company or template type."
                status="404"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default requireAuth(BreakDownAnalysisScreen, COMPANY_ADMIN_ROLE_ACCESS,BREAKDOWN_PERMISSION);
